import React from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';
import { useFlagsService } from '@atlassian/jira-flags';
import { FormattedMessage } from '@atlassian/jira-intl';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import { FormStatus } from '@atlassian/proforma-common-core/form-system-models';
import { DefaultMeatballMenu, type MeatballMenuItemProps } from '@atlassian/proforma-form-system';
import { Action, ActionSubject } from '@atlassian/proforma-utils/analytics';
import { CloseFormResult } from '../../../../controllers/use-handle-close-form/types.tsx';
import { DeleteFormResult } from '../../../../controllers/use-handle-delete-form/types.tsx';
import { UpdateFormVisibilityResult } from '../../../../controllers/use-handle-update-form-visibility/types.tsx';
import { OpenFormResult } from '../../../../controllers/use-reopen-form-with-indexitem/types.tsx';
import { messages } from './messages.tsx';
import type { JiraFormCardMenuProps } from './types.tsx';
import { getSimpleFormAnalytics } from './utils.tsx';

export const JiraFormCardMenu = ({
	form,
	formStore,
	permissions,
	onDeleteForm,
	onDownloadFormPdf,
	onDownloadFormXlsx,
	onUpdateFormVisibility,
	onReopenForm,
	onCloseForm,
	onEditForm,
	projectType,
}: JiraFormCardMenuProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag } = useFlagsService();
	const showErrorFlag = (title: MessageDescriptor, description?: MessageDescriptor) =>
		showFlag({
			title,
			description: description || undefined,
			type: 'error',
			messageId:
				'proforma-panel-in-issue-view-ui.ui.issue-view-forms.jira-form-card-index.jira-form-card-menu.show-flag.error',
			messageType: 'transactional',
		});
	const showSuccessFlag = (title: MessageDescriptor) =>
		showFlag({
			title,
			isAutoDismiss: true,
			type: 'success',
			messageId:
				'proforma-panel-in-issue-view-ui.ui.issue-view-forms.jira-form-card-index.jira-form-card-menu.show-flag.success',
			messageType: 'transactional',
		});

	const canSaveAndSubmit = permissions.save && permissions.submit;
	const canReopenAndUnlock = permissions.reopen && permissions.unlock;

	// "Edit form"
	const handleEditForm = async () => {
		fireUIAnalytics(
			createAnalyticsEvent({
				actionSubject: ActionSubject.DROPDOWN_ITEM,
				action: Action.CLICKED,
			}),
			'editForm',
			getSimpleFormAnalytics(form.id, form.projectFormId, form.status, form.internal),
		);
		await onEditForm(form.id, formStore);
	};
	const disableEditForm =
		!canSaveAndSubmit ||
		(form.status === FormStatus.Submitted && !permissions.reopen) ||
		(form.status === FormStatus.Locked && !canReopenAndUnlock);
	const editFormMenuItem: MeatballMenuItemProps = {
		onClick: handleEditForm,
		isDisabled: disableEditForm,
		children: <FormattedMessage {...messages.editForm} />,
	};

	// "Open customer edits"
	const handleReopenForm = async () => {
		const result = await onReopenForm(form, formStore);
		if (result === OpenFormResult.Success) {
			showSuccessFlag(
				projectType === 'service_desk'
					? messages.openSuccessTitle
					: messages.openSuccessTitleNonJsm,
			);
		} else if (result === OpenFormResult.Failure) {
			showErrorFlag(
				projectType === 'service_desk'
					? messages.openFailureTitle
					: messages.openFailureTitleNonJsm,
			);
		}
	};
	const openCustomerEditMenuItem = {
		onClick: handleReopenForm,
		isDisabled: !permissions.reopen || (form.status === FormStatus.Locked && !canReopenAndUnlock),
		children: <FormattedMessage {...messages.openCustomerEdits} />,
	};

	// "Close customer edits"
	const handleCloseForm = async () => {
		const result = await onCloseForm(form.id, formStore);
		if (result === CloseFormResult.Success) {
			showSuccessFlag(messages.closeSuccessTitle);
		} else if (result === CloseFormResult.ValidationFailed) {
			showErrorFlag(messages.closeValidationFailedTitle, messages.closeValidationFailedDescription);
		}
	};
	const closeCustomerEditMenuItem = {
		onClick: handleCloseForm,
		isDisabled: !canSaveAndSubmit,
		children: <FormattedMessage {...messages.closeCustomerEdits} />,
	};

	let openCloseMenuItem: MeatballMenuItemProps | undefined;
	if (!form.internal && form.status === FormStatus.Open) {
		openCloseMenuItem = closeCustomerEditMenuItem;
	} else if (!form.internal && form.status !== FormStatus.Open) {
		openCloseMenuItem = openCustomerEditMenuItem;
	}

	// "Close for edits" (non JSM projects)
	const handleCloseFormNonJSM = async () => {
		const result = await onCloseForm(form.id, formStore);
		if (result === CloseFormResult.Success) {
			showSuccessFlag(messages.closeFormSuccessTitle);
		} else if (result === CloseFormResult.ValidationFailed) {
			showErrorFlag(
				messages.closeFormValidationFailedTitle,
				messages.closeValidationFailedDescription,
			);
		}
	};

	const openCustomerEditMenuItemNonJSM = {
		onClick: handleReopenForm,
		isDisabled: !permissions.reopen || (form.status === FormStatus.Locked && !canReopenAndUnlock),
		children: <FormattedMessage {...messages.openFormEdits} />,
	};

	const closeCustomerEditMenuItemNonJSM = {
		onClick: handleCloseFormNonJSM,
		isDisabled: !canSaveAndSubmit,
		children: <FormattedMessage {...messages.closeFormEdits} />,
	};

	const openCloseMenuItemNonJSM: MeatballMenuItemProps | undefined =
		form.status === FormStatus.Open
			? closeCustomerEditMenuItemNonJSM
			: openCustomerEditMenuItemNonJSM;

	// "Make Internal"/"Make External"
	const handleUpdateFormVisibility = async () => {
		const result = await onUpdateFormVisibility(form.id, !form.internal, form.restrictionStatus);
		if (result === UpdateFormVisibilityResult.SUCCESS) {
			showSuccessFlag(form.internal ? messages.externalFormSuccess : messages.internalFormSuccess);
		} else if (result === UpdateFormVisibilityResult.FAILURE) {
			showErrorFlag(
				form.internal ? messages.externalFormFailureTitle : messages.internalFormFailureTitle,
			);
		}
	};

	const toggleVisibilityMenuItem: MeatballMenuItemProps = {
		isDisabled: !permissions.toggleAccess,
		tooltip: (
			<FormattedMessage
				{...(form.internal ? messages.makeExternalTooltip : messages.makeInternalTooltip)}
			/>
		),
		onClick: handleUpdateFormVisibility,
		children: form.internal ? (
			<FormattedMessage {...messages.makeExternal} />
		) : (
			<FormattedMessage {...messages.makeInternal} />
		),
	};

	// "Download Pdf"
	const downloadPdfMenuItem: MeatballMenuItemProps = {
		onClick: () => onDownloadFormPdf(form.id, form.name),
		isDisabled: !permissions.download,
		tooltip: <FormattedMessage {...messages.downloadPdfTooltip} />,
		children: <FormattedMessage {...messages.downloadPdf} />,
	};

	// "Download Xlsx"
	const downloadXlsxMenuItem: MeatballMenuItemProps = {
		onClick: () => onDownloadFormXlsx(form.id, form.name),
		isDisabled: !permissions.download,
		tooltip: <FormattedMessage {...messages.downloadXlsxTooltip} />,
		children: <FormattedMessage {...messages.downloadXlsx} />,
	};

	// "Delete Form"
	const handleDeleteForm = async () => {
		fireUIAnalytics(
			createAnalyticsEvent({
				actionSubject: ActionSubject.DROPDOWN_ITEM,
				action: Action.CLICKED,
			}),
			'deleteForm',
			getSimpleFormAnalytics(form.id, form.projectFormId, form.status, form.internal),
		);
		const result = await onDeleteForm(form.id, form.name);
		if (result === DeleteFormResult.Success) {
			showSuccessFlag(messages.deleteSuccessTitle);
		} else if (result === DeleteFormResult.Failure) {
			showErrorFlag(messages.deleteFailureTitle);
		}
	};
	const deleteMenuItem: MeatballMenuItemProps = {
		isDisabled: !canSaveAndSubmit || form.status !== FormStatus.Open,
		tooltip: <FormattedMessage {...messages.deleteTooltip} />,
		onClick: handleDeleteForm,
		children: <FormattedMessage {...messages.delete} />,
	};

	const menuItems = [
		projectType === 'service_desk' || form.status === FormStatus.Open
			? editFormMenuItem
			: undefined,
		projectType === 'service_desk' ? openCloseMenuItem : openCloseMenuItemNonJSM,
		projectType === 'service_desk' ? toggleVisibilityMenuItem : undefined,
		downloadPdfMenuItem,
		downloadXlsxMenuItem,
		deleteMenuItem,
	];
	return (
		<DefaultMeatballMenu
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			items={menuItems.filter((i) => i !== undefined) as MeatballMenuItemProps[]}
		/>
	);
};
